import { supabase } from './supabase'

// API 基础配置
const API_BASE_URL = import.meta.env.VITE_SUPABASE_URL

// 获取用户策略列表
export const getUserStrategies = async (params?: {
  search?: string
  type?: string
  limit?: number
  offset?: number
}) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const url = new URL(`${API_BASE_URL}/functions/v1/get-user-strategies`)
    if (params?.search) url.searchParams.set('search', params.search)
    if (params?.type) url.searchParams.set('type', params.type)
    if (params?.limit) url.searchParams.set('limit', params.limit.toString())
    if (params?.offset) url.searchParams.set('offset', params.offset.toString())

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error fetching user strategies:', error)
    throw error
  }
}

// 获取用户信息
export const getUserProfile = async (userId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const url = new URL(`${API_BASE_URL}/functions/v1/get-user-profile`)
    url.searchParams.set('user_id', userId)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error fetching user profile:', error)
    throw error
  }
}

// 获取策略详情
export const getStrategyDetail = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const url = new URL(`${API_BASE_URL}/functions/v1/get-strategy-dashboard`)
    url.searchParams.set('strategy_id', strategyId)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error fetching strategy detail:', error)
    throw error
  }
}

// 创建新策略
export const createStrategy = async (strategyData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/create-strategy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(strategyData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error creating strategy:', error)
    throw error
  }
}

// 更新策略
export const updateStrategy = async (strategyData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-strategy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(strategyData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating strategy:', error)
    throw error
  }
}

// 更新策略逻辑
export const updateStrategyLogic = async (logicData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-strategy-logic`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logicData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating strategy logic:', error)
    throw error
  }
}

// 删除策略
export const deleteStrategy = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/delete-strategy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ strategy_id: strategyId }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error deleting strategy:', error)
    throw error
  }
}

// 添加策略参数
export const addStrategyParameter = async (parameterData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/add-strategy-parameter`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(parameterData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error adding strategy parameter:', error)
    throw error
  }
}

// 更新策略参数
export const updateStrategyParameter = async (parameterData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-strategy-parameter`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(parameterData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating strategy parameter:', error)
    throw error
  }
}

// 删除策略参数
export const deleteStrategyParameter = async (parameterId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/delete-strategy-parameter`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ parameter_id: parameterId }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.success
  } catch (error) {
    console.error('Error deleting strategy parameter:', error)
    throw error
  }
}

// 获取手动测试统计数据
export const getManualTestStats = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const url = new URL(`${API_BASE_URL}/functions/v1/get-manual-test-stats`)
    url.searchParams.set('strategy_id', strategyId)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error fetching manual test stats:', error)
    throw error
  }
}

// 获取手动测试交易记录
export const getManualTestTrades = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const url = new URL(`${API_BASE_URL}/functions/v1/get-manual-trades-for-strategy`)
    url.searchParams.set('strategy_id', strategyId)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error fetching manual test trades:', error)
    throw error
  }
}

// 提交手动测试交易记录
export const submitManualTestTrade = async (tradeData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/submit-manual-test-trade`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tradeData),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error submitting manual test trade:', error)
    throw error
  }
}

// 更新手动测试交易记录
export const updateManualTestTrade = async (tradeId: string, updates: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-manual-test-trade`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ trade_id: tradeId, ...updates }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating manual test trade:', error)
    throw error
  }
}

// 删除手动测试交易记录
export const deleteManualTestTrade = async (tradeId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/delete-manual-test-trade`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ trade_id: tradeId }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error deleting manual test trade:', error)
    throw error
  }
}



// 更新用户设置
export const updateUserSettings = async (settingsType: string, settingsData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-user-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        settings_type: settingsType,
        settings_data: settingsData
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating user settings:', error)
    throw error
  }
}

// 获取用户设置
export const getUserSettings = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/get-user-settings`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error getting user settings:', error)
    throw error
  }
}

// 获取实盘追踪数据
export const getLiveTracking = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { data, error } = await supabase
      .from('live_tracking')
      .select('*')
      .eq('strategy_id', strategyId)
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  } catch (error) {
    console.error('Error fetching live tracking:', error)
    throw error
  }
}

// 创建实盘追踪记录
export const createLiveTracking = async (trackingData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { data, error } = await supabase
      .from('live_tracking')
      .insert([trackingData])
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating live tracking:', error)
    throw error
  }
}

// 更新实盘追踪记录
export const updateLiveTracking = async (trackingId: string, updates: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { data, error } = await supabase
      .from('live_tracking')
      .update(updates)
      .eq('id', trackingId)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  } catch (error) {
    console.error('Error updating live tracking:', error)
    throw error
  }
}

// 删除实盘追踪记录
export const deleteLiveTracking = async (trackingId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { error } = await supabase
      .from('live_tracking')
      .delete()
      .eq('id', trackingId)

    if (error) {
      throw error
    }

    return true
  } catch (error) {
    console.error('Error deleting live tracking:', error)
    throw error
  }
}

// 获取回测结果
export const getBacktestResults = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { data, error } = await supabase
      .from('backtest_results')
      .select('*')
      .eq('strategy_id', strategyId)
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  } catch (error) {
    console.error('Error fetching backtest results:', error)
    throw error
  }
}

// 提交回测结果
export const submitBacktestResult = async (backtestData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/submit-backtest-result`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backtestData),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error submitting backtest result:', error)
    throw error
  }
} 

// 更新回测结果
export const updateBacktestResult = async (backtestId: string, updates: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-backtest-result`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ backtest_id: backtestId, ...updates }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating backtest result:', error)
    throw error
  }
}

// 删除回测结果
export const deleteBacktestResult = async (backtestId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/delete-backtest-result`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ backtest_id: backtestId }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error deleting backtest result:', error)
    throw error
  }
}

// 获取研究笔记
export const getStrategyNotes = async (strategyId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const { data, error } = await supabase
      .from('strategy_notes')
      .select('*')
      .eq('strategy_id', strategyId)
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  } catch (error) {
    console.error('Error fetching strategy notes:', error)
    throw error
  }
}

// 记录策略笔记
export const recordStrategyNote = async (noteData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/record-strategy-note`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noteData),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error recording strategy note:', error)
    throw error
  }
}

// 更新研究笔记
export const updateStrategyNote = async (noteId: string, updates: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/update-strategy-note`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ note_id: noteId, ...updates }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error updating strategy note:', error)
    throw error
  }
}

// 删除研究笔记
export const deleteStrategyNote = async (noteId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('No session found')
    }

    const response = await fetch(`${API_BASE_URL}/functions/v1/delete-strategy-note`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ note_id: noteId }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error deleting strategy note:', error)
    throw error
  }
} 